import React, { useState } from 'react';
import { Form, Input, Button, Space, List, Avatar, Typography, Divider } from 'antd';
import { SendOutlined, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import type { Comment } from '@/types/common';
import dayjs from 'dayjs';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const { TextArea } = Input;
const { Text } = Typography;

interface CommentsFormSectionProps {
  comments?: Comment[];
  onAddComment?: (comment: Omit<Comment, 'createdAt'>) => void;
  onUpdateComment?: (commentIndex: number, content: string) => void;
  onDeleteComment?: (commentIndex: number) => void;
  showForm?: boolean;
  maxHeight?: string;
}

const CommentsFormSection: React.FC<CommentsFormSectionProps> = ({
  comments = [],
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  showForm = true,
  maxHeight = '400px'
}) => {
  const { t } = useTranslation(['common']);
  const { firestoreUser } = useAuth();
  const [form] = Form.useForm();
  const [editingCommentIndex, setEditingCommentIndex] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<number | null>(null);

  const handleSubmit = async (values: { content: string }) => {
    if (!firestoreUser || !values.content.trim()) return;

    // Determine user type from roles
    const userType = firestoreUser.roles.includes('resident') ? 'resident' : 
                    firestoreUser.roles.includes('vendor') ? 'vendor' : 'employee';

    const newComment: Omit<Comment, 'createdAt'> = {
      content: values.content.trim(),
      author: {
        id: firestoreUser.id,
        name: firestoreUser.displayName || firestoreUser.email || 'Unknown User',
        type: userType,
        profilePicture: firestoreUser.profilePicture
      }
    };

    onAddComment?.(newComment);
    form.resetFields();
  };

  const handleEdit = (comment: Comment, index: number) => {
    setEditingCommentIndex(index);
    setEditingContent(comment.content);
  };

  const handleSaveEdit = () => {
    if (editingCommentIndex !== null && editingContent.trim()) {
      onUpdateComment?.(editingCommentIndex, editingContent.trim());
      setEditingCommentIndex(null);
      setEditingContent('');
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentIndex(null);
    setEditingContent('');
  };

  const handleDelete = (index: number) => {
    setCommentToDelete(index);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (commentToDelete !== null) {
      onDeleteComment?.(commentToDelete);
      setCommentToDelete(null);
      setIsDeleteModalVisible(false);
    }
  };

  const handleDeleteCancel = () => {
    setCommentToDelete(null);
    setIsDeleteModalVisible(false);
  };

  const canEditComment = (comment: Comment) => {
    return firestoreUser?.id === comment.author.id;
  };

  const canDeleteComment = (comment: Comment) => {
    return firestoreUser?.id === comment.author.id;
  };

  return (
    <div>
      {/* Comments List */}
      <div style={{ maxHeight, overflowY: 'auto', marginBottom: showForm ? 16 : 0 }}>
        <List
          dataSource={comments}
          locale={{ emptyText: t('common:noComments') }}
          renderItem={(comment, index) => (
            <List.Item
              style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}
            >
              <List.Item.Meta
                avatar={
                  <Avatar 
                    icon={<UserOutlined />} 
                    src={comment.author.profilePicture?.path}
                  />
                }
                title={
                  <Space>
                    <Text strong>{comment.author.name}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {dayjs(comment.createdAt).format('MMM D, YYYY HH:mm')}
                    </Text>
                  </Space>
                }
                description={
                  editingCommentIndex === index ? (
                    <div style={{ marginTop: 8 }}>
                      <TextArea
                        value={editingContent}
                        onChange={(e) => setEditingContent(e.target.value)}
                        rows={2}
                        style={{ marginBottom: 8 }}
                      />
                      <Space>
                        <Button size="small" type="primary" onClick={handleSaveEdit}>
                          {t('common:actions.save')}
                        </Button>
                        <Button size="small" onClick={handleCancelEdit}>
                          {t('common:actions.cancel')}
                        </Button>
                      </Space>
                    </div>
                  ) : (
                    <Text style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</Text>
                  )
                }
              />
              {editingCommentIndex !== index && (
                <Space>
                  {canEditComment(comment) && (
                    <Button 
                      type="text" 
                      size="small" 
                      onClick={() => handleEdit(comment, index)}
                    >
                      {t('common:actions.edit')}
                    </Button>
                  )}
                  {canDeleteComment(comment) && (
                    <Button 
                      type="text" 
                      size="small" 
                      danger 
                      onClick={() => handleDelete(index)}
                    >
                      {t('common:actions.delete')}
                    </Button>
                  )}
                </Space>
              )}
            </List.Item>
          )}
        />
      </div>

      {/* Add Comment Form */}
      {showForm && onAddComment && (
        <div>
          <Divider />
          <Form form={form} onFinish={handleSubmit}>
            <Form.Item
              name="content"
              rules={[
                { required: true, message: t('common:validation.required') },
                { min: 1, message: t('common:validation.minLength', { min: 1 }) }
              ]}
            >
              <TextArea
                rows={3}
                placeholder={t('common:addCommentPlaceholder')}
                maxLength={1000}
                showCount
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SendOutlined />}
                disabled={!firestoreUser}
              >
                {t('common:addComment')}
              </Button>
            </Form.Item>
          </Form>
        </div>
      )}

      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title={t('common:confirmDeleteComment')}
      />
    </div>
  );
};

export default CommentsFormSection; 